import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Alert,
  Tooltip,
  DialogContentText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  AdminPanelSettings as PermissionsIcon,
  LockReset as ClearPinIcon,
} from '@mui/icons-material';
import UserPermissionsDialog from './UserPermissionsDialog';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getPermissionCatalog,
  getUserPermissions,
  PERMISSION_CATALOG_KEY,
  userPermissionsKey,
} from '../../services/userPermissionsService';
import { customizedCount, firstNameOf, roleChangeNarrowing } from './permissionEditorModel';
/**
 * The ONE spelling of the iPhone line. The editor's dialog renders the same component
 * with the same sentence; neither renderer retypes it.
 *
 * It is a NOTE rather than #66's gate since Ali's decision #79 (2026-09-18): iOS 1.3.0
 * ships FIRST and this screen follows it, so "update the app" is an instruction a clinic
 * can act on and nothing here is held. The hazard below the floor is unchanged — a
 * per-op PERMISSION_DENIED falls into SyncEngine's default arm, which discards the
 * server's sentence and burns a retry; after three the op is retained locally, hidden
 * from the unsynced count and never revived by a re-grant — but the web cannot tell
 * which build anyone runs, so the sentence addresses the clinic instead of gating a
 * save. It deliberately never says "removes it from their phone": a revoke narrows
 * FUTURE pulls only, and there is no retroactive wipe.
 */
import { IosVersionNote } from './IosNarrowingGate';
import { useRBAC } from '../../auth/useRBAC';
import { teamMembersControls } from '../../auth/pageControls';
import { TEAM_MEMBERS_PERMISSION } from '../../auth/screenAccess';
import { useAuth } from '../../auth/useAuth';
import { ROLE_LABELS, ROLE_PICKER_ORDER, resolveAssignableRoles } from '../../auth/roles';
import type { UserRoleSlug } from '../../auth/roles';
import api, { userApi } from '../../utils/api';
import { useSnackbar } from 'notistack';
import { db, withOfflineFallback, readClinic, readCurrentUser, OfflineNotice, useIsOffline } from '../../offline';

import type { TeamUser } from './teamTypes';

/**
 * The Team row shape, owned by `teamTypes.ts` since P6 so the permission editor can
 * take a row as a PROP without importing the screen that renders it — the import
 * cycle that module exists to break. The alias is deliberate: every `User`
 * annotation in this file keeps working, and there is exactly one declaration of
 * the shape for the row and the dialog to agree on.
 */
type User = TeamUser;

interface UserFormData {
  email: string;
  name: string;
  role: User['role'];
}

/**
 * The only part of `GET /users/me` this screen reads. Typed as `unknown` on
 * purpose: `assignableRoles` is ADDITIVE (P5 design §C), so an older backend, a
 * cached payload and the offline replica row all answer without it, and
 * `resolveAssignableRoles` is the one place that decides what a non-answer means.
 *
 * ⚠ NOT taken from `types/api.generated.ts`: that route's 200 is pinned
 * `@ApiResponse({ status: 200, type: Object })` (users.controller.ts:50, and the
 * pin is load-bearing for INV-85), so the generated response type is
 * `Record<string, never>` and carries no keys at all.
 */
interface MeShape {
  assignableRoles?: unknown;
  /** The signed-in user's `User.id` — NOT the Firebase uid. INV-25 says the two
   *  DIVERGE for every invited user, and the permissions dialog compares this to
   *  the target's `userId` to decide whether a save is a SELF edit (P6 design §D).
   *  Read here only to hand it over; this screen makes no decision on it. */
  id?: string;
}

/**
 * The role a NEW user's form starts on, given the options THIS caller has.
 *
 * 'VETERINARIAN' is the historical default and stays the default whenever it is
 * actually assignable — but it is only a default, never a guarantee. P4 gates a
 * permission grant on the ACTOR's own permissions rather than on the target's
 * rank, so a VETERINARY_TECHNICIAN can legitimately hold `manage:users`, and
 * `assignableRolesFor` then answers its own rank and below. An unclamped default
 * would leave the Select on a value with no `MenuItem`: a blank trigger, MUI's
 * "out-of-range value" console error, and a name-only submit still POSTing a role
 * the API refuses — the ADD-path twin of the own-role rule in `roleOptions`.
 *
 * The fallback is the LAST option because `ROLE_PICKER_ORDER` is rank order, so
 * the last one offered is the least-privileged: the conservative default for a
 * control that creates users. `roleOptions` is never empty —
 * `resolveAssignableRoles` falls back to the legacy four rather than to `[]`.
 */
const defaultRoleFor = (options: UserRoleSlug[]): UserRoleSlug =>
  options.includes('VETERINARIAN') ? 'VETERINARIAN' : options[options.length - 1];

/**
 * The Team screen's offline block, EXPORTED so the no-long-dash guard on this surface can walk it
 * (#86) — an inline JSX string is copy no test can reach by name. User ops touch
 * Firebase/HubSpot/Postmark, so they are online-only and none of them is ever queued.
 */
export const TEAM_OFFLINE_NOTICE =
  "Team management needs an internet connection. Invitations and account changes can't be queued.";

export const UserManagement: React.FC = () => {
  const rbac = useRBAC();
  const { hasPermission } = rbac;
  const can = teamMembersControls(rbac);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isOffline = useIsOffline();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  // The seed only — `roleOptions` is not in scope yet. Every path that OPENS the
  // dialog re-seeds this through `defaultRoleFor`, and the render-phase clamp
  // below catches the case where the options narrow while it is already open.
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    role: 'VETERINARIAN',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** The row whose permissions are open. The dialog is MOUNTED on a target rather
   *  than kept mounted with `open=false`, so its server state hydrates once per
   *  target in its own effect instead of having to re-hydrate on a prop change. */
  const [permissionsTarget, setPermissionsTarget] = useState<User | null>(null);
  /** The role the open edit form is asking for, once it differs from the row's
   *  own. Non-null is exactly "the confirm is showing". */
  const [pendingRoleChange, setPendingRoleChange] = useState<UserRoleSlug | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Update current time every second for live countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const { data: users = [] } = useQuery<User[]>(
    'users',
    withOfflineFallback(
      async () => {
        const response = await api.get('/users');
        return response.data;
      },
      // Replica user rows are scalar-only (no REST `clinic` include) and the
      // row render hard-derefs user.clinic.name — hydrate from the synced
      // clinic row or a bare replica row would crash the table.
      async () => {
        const [rows, clinic] = await Promise.all([db.users.toArray(), readClinic()]);
        return rows.map((u: any) => ({ ...u, clinic: { name: clinic?.name ?? '' } })) as User[];
      },
    ),
  );

  // Which roles THIS caller may assign. The backend decides it — the rank
  // ceiling, the OWNER carve-out, and the `CSR_ROLE_ASSIGNABLE` flag, which held
  // CSR back until the iOS build that knows the role was live (P5 design §C) and
  // since 2026-09-21 (#65) is the emergency OFF rather than the launch switch —
  // and the picker only renders what it is handed.
  //
  // ⚠ SAME query key, fetcher and staleTime as App.tsx's three `['current-user-me']`
  // readers (the layout shell at App.tsx:184-196, `FaroUserSync`, the onboarding
  // gate). They share the key, so the fns must behave identically or the cache
  // flip-flops by mount order (App.tsx:678). Sharing it also means the shell has
  // usually already paid for this request.
  //
  // ⚠ SIX verbatim copies of this block exist now — the three in App.tsx, both
  // onboarding components, and this one — and they are owed a shared
  // `useCurrentUserMe()`. Deliberately NOT extracted here: `SetupChecklistDrawer`
  // and `WelcomeModal` type the result AND omit `enabled: !!user`, so unifying the
  // six changes when those two fire. That is a behaviour decision with its own
  // RED/GREEN, not a mechanical move, and it does not belong in this screen's task.
  const { data: me } = useQuery(
    ['current-user-me'],
    withOfflineFallback(
      async () => {
        const res = await api.get('/users/me');
        return res.data;
      },
      () => readCurrentUser(user?.uid, user?.email ?? undefined),
    ),
    { staleTime: 5 * 60 * 1000, enabled: !!user },
  );

  /**
   * The Permissions affordance's own gate — STRICTLY NARROWER than the tab's
   * `manage:users` (the early return below), so a user can legitimately see this
   * table and not this action. The other direction is closed in the CATALOG, not
   * here: `manage:roles_permissions` requires `manage:users` (P6 design §B, Ali
   * default #74), which is what keeps every permissions admin the editor can
   * create able to reach the Team tab at all.
   */
  const canManagePermissions = can.editPermissions;

  /**
   * What the ROLE-CHANGE confirm needs, and nothing else. BOTH routes are gated on
   * `manage:roles_permissions` (P6 design §B), so they are only ever asked for by
   * an actor who holds it — an actor with `manage:users` alone gets the honest
   * fallback in `roleChange` below rather than a 403 in the console. They are
   * keyed the way the editor keys them, so a save in the dialog refreshes this.
   */
  const { data: targetPermissions } = useQuery(
    userPermissionsKey(selectedUser?.id ?? ''),
    () => getUserPermissions(selectedUser!.id),
    { enabled: canManagePermissions && isDialogOpen && !!selectedUser, staleTime: 30 * 1000 },
  );
  const { data: permissionCatalog } = useQuery(PERMISSION_CATALOG_KEY, getPermissionCatalog, {
    // Welded facts, not clinic data: it cannot go stale within a session.
    enabled: canManagePermissions && isDialogOpen && !!selectedUser,
    staleTime: Infinity,
  });

  const roleOptions = useMemo<UserRoleSlug[]>(() => {
    const offered = new Set(resolveAssignableRoles((me as MeShape | undefined)?.assignableRoles));
    // The edited user's OWN role is always offered, even when this caller could
    // never assign it. Without this, an ADMIN opening the Owner row would put the
    // Select on an out-of-range value: a blank trigger, an MUI warning, and a
    // name-only edit still posting `role: 'OWNER'`. It also covers a CSR user
    // surviving a flag flip back to OFF.
    if (selectedUser) offered.add(selectedUser.role);
    return ROLE_PICKER_ORDER.filter((role) => offered.has(role));
  }, [me, selectedUser]);

  // `/users/me` is usually warm from the shell's own `['current-user-me']` read,
  // but on a cold load it resolves while this screen is already interactive — so
  // the option list can NARROW under an OPEN dialog. Clamp the form back onto it
  // rather than leave the Select sitting out of range. The EDIT path is never
  // clamped: `roleOptions` always contains the edited user's own role.
  //
  // ⚠ DURING RENDER, not in an effect, and that is the whole point: React discards
  // this render and re-runs the component with the new state BEFORE committing, so
  // the out-of-range value never reaches the DOM. An effect would commit it first —
  // one frame of a blank trigger plus MUI's "out-of-range value" console error, the
  // exact symptom this clamp exists to prevent. It cannot loop: after the clamp the
  // role IS in `roleOptions` (which is never empty) and the condition is false.
  if (isDialogOpen && !roleOptions.includes(formData.role)) {
    setFormData((prev) => ({ ...prev, role: defaultRoleFor(roleOptions) }));
  }

  const paginatedUsers = useMemo(
    () => users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [users, page, rowsPerPage],
  );

  // Reset to first page if the underlying data shrinks below the current page
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(users.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [users.length, rowsPerPage, page]);

  // Count the number of owners in the clinic
  const ownerCount = users.filter(user => user.role === 'OWNER').length;

  // Check if the selected user is the only owner
  const isOnlyOwner = selectedUser?.role === 'OWNER' && ownerCount === 1;

  // Helper function to check if a role option should be disabled
  const isRoleDisabled = (role: User['role']) => {
    if (!selectedUser) return false;
    // Disable non-OWNER roles if this user is the only owner
    return isOnlyOwner && role !== 'OWNER';
  };

  /**
   * The pending role change, measured. `precise` is the ONLY arm allowed to state
   * a number or name a consequence — it needs the target's effective set and the
   * catalog, both gated on `manage:roles_permissions`.
   *
   * The other arm cannot measure anything, so it shows the note on EVERY role
   * change. Rank is not the test and never was: a role change deletes every
   * per-user override, not only the ones the new role's defaults drop, so a
   * front-desk user holding a granted sync-affecting ability loses it on the way
   * UP to VETERINARY_TECHNICIAN — a narrowing with no drop in
   * `ROLE_PICKER_ORDER`. (The converse fails too: VETERINARIAN and
   * VETERINARY_TECHNICIAN hold the SAME defaults,
   * backend/src/auth/permissions.ts:381-382, so that move narrows nothing while
   * the index rises.) The sentence asserts nothing about THIS change for that
   * second reason — it is about the person's app version, not about what moved —
   * which is why one spelling serves both arms.
   */
  const roleChange = useMemo(() => {
    if (!selectedUser || !pendingRoleChange) return null;
    const precise = canManagePermissions && !!targetPermissions && !!permissionCatalog;
    const syncNarrowed = precise
      ? roleChangeNarrowing(
          targetPermissions!.effective,
          permissionCatalog!.roleDefaults[pendingRoleChange] ?? [],
          permissionCatalog!,
        ).syncNarrowed
      : [];
    return {
      nextRole: pendingRoleChange,
      precise,
      syncNarrowed,
      customized: precise ? customizedCount(targetPermissions!) : 0,
      // The SAME condition the gate used, kept exactly (#79 changed what is shown,
      // not when). Unmeasured is `true`, not a guess at the direction: an empty list
      // on that arm means "could not be measured", never "nothing narrows".
      showIosNote: precise ? syncNarrowed.length > 0 : true,
    };
  }, [selectedUser, pendingRoleChange, canManagePermissions, targetPermissions, permissionCatalog]);

  const createUser = useMutation(
    async (data: UserFormData) => {
      const response = await api.post('/users', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        handleCloseDialog();
      },
      onError: async (error: any) => {
        // Suppress the error from appearing in console since we handle it gracefully
        const message = error?.response?.data?.message || 'An error occurred while creating the user.';
        
        // For other errors, show the error message
        setErrorMessage(message);
      },
    }
  );

  const updateUser = useMutation(
    async ({ id, data }: { id: string; data: Partial<UserFormData> }) => {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries('users');
        // A role change clears this user's overrides in the same transaction, so
        // the cached view the confirm reads its count from is stale the moment
        // the PUT succeeds. Cache hygiene, not a rendered state — the observable
        // effect is the next time this row's editor or confirm is opened.
        queryClient.invalidateQueries(userPermissionsKey(variables.id));
        handleCloseDialog();
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message ||
          'An error occurred while updating the user.';
        setErrorMessage(message);
      },
    }
  );

  const deleteUser = useMutation(
    async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 
          'An error occurred while deleting the user.';
        // Row actions surface refusals through notistack; the in-dialog Alert only
        // exists while the edit dialog is open (`<Dialog open={isDialogOpen}>`
        // below) and a delete happens from a row with none, so a message set there
        // would simply vanish. P6 makes two more 400s reachable here: deleting
        // yourself, and deleting someone who has an administrative audit history —
        // which is every admin who has used the permissions editor.
        enqueueSnackbar(message, { variant: 'error' });
      },
    }
  );

  // Workstation lock (plan T6, D4): a cleared person sets a new PIN themselves after a password sign-in.
  const clearPin = useMutation(
    async (id: string) =>
      (await api.delete(`/users/${id}/approval-pin`)).data as { cleared?: boolean } | undefined,
  );

  const handleClearPin = async (target: User) => {
    if (
      !window.confirm(
        `Clear ${target.name}'s PIN? They can set a new one after signing in with their password.`,
      )
    ) {
      return;
    }
    try {
      const result = await clearPin.mutateAsync(target.id);
      enqueueSnackbar(
        result?.cleared ? `${target.name}'s PIN is cleared.` : `${target.name} has no PIN to clear.`,
        { variant: result?.cleared ? 'success' : 'info' },
      );
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Could not clear the PIN.', { variant: 'error' });
    }
  };

  const sendInvitation = useMutation(
    async (userId: string) => {
      return await userApi.sendInvitationEmail(userId);
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          enqueueSnackbar(data.message, { variant: 'success' });
          queryClient.invalidateQueries('users');
        } else {
          enqueueSnackbar(data.message, { variant: 'error' });
        }
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 
          'Failed to send invitation email. Please try again.';
        enqueueSnackbar(message, { variant: 'error' });
      },
    }
  );

  // Helper function to check if invitation can be sent
  const canSendInvitation = (user: User): boolean => {
    // User has completed setup, can't send invitation
    if (user.hasCompletedSetup === true) {
      return false;
    }
    
    // Check cooldown period (2 minutes = 120,000 ms)
    if (user.lastInvitationSentAt) {
      const timeSinceLastSend = Date.now() - user.lastInvitationSentAt;
      const twoMinutesInMs = 2 * 60 * 1000;
      return timeSinceLastSend > twoMinutesInMs;
    }
    
    // No invitation sent yet, can send
    return true;
  };

  // Helper function to get user status with invitation info
  const getUserStatus = (user: User): { label: string; color: 'success' | 'warning' | 'default' | 'error' } => {
    // User has completed setup
    if (user.hasCompletedSetup === true) {
      return { label: 'Active', color: 'success' };
    }
    
    // User invited but hasn't set up yet
    if (user.invitationSentAt) {
      const sentDate = new Date(user.invitationSentAt);
      const formattedDate = `${sentDate.toLocaleDateString()} ${sentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      return { label: `Invited (${formattedDate})`, color: 'warning' };
    }
    
    // User created but not invited yet
    return { label: 'Not Invited', color: 'default' };
  };

  // Helper function to get time remaining for cooldown
  const getCooldownInfo = (user: User): { canSend: boolean; message: string } | null => {
    if (!user.lastInvitationSentAt || user.hasCompletedSetup) {
      return null;
    }
    
    const timeSinceLastSend = currentTime - user.lastInvitationSentAt;
    const twoMinutesInMs = 2 * 60 * 1000;
    
    // Handle clock skew: if timeSinceLastSend is negative (client clock behind server),
    // treat it as if no time has passed yet (still in cooldown)
    if (timeSinceLastSend < 0) {
      return {
        canSend: false,
        message: 'Please wait 2m 0s before resending invitation (checking...)'
      };
    }
    
    if (timeSinceLastSend < twoMinutesInMs) {
      // Clamp to ensure we never show negative time
      const secondsRemaining = Math.max(0, Math.ceil((twoMinutesInMs - timeSinceLastSend) / 1000));
      const minutes = Math.floor(secondsRemaining / 60);
      const seconds = secondsRemaining % 60;
      const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
      return {
        canSend: false,
        message: `Please wait ${timeStr} before resending invitation`
      };
    }
    
    return {
      canSend: true,
      message: 'Ready to send invitation'
    };
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
      });
    } else {
      setSelectedUser(null);
      setFormData({
        email: '',
        name: '',
        // Not the literal: a caller who cannot assign VETERINARIAN would open the
        // dialog on an option that is not there (see `defaultRoleFor`).
        role: defaultRoleFor(roleOptions),
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedUser(null);
    setFormData({
      email: '',
      name: '',
      role: defaultRoleFor(roleOptions),
    });
    setErrorMessage(null);
    setPendingRoleChange(null);
  };

  const submitForm = async () => {
    try {
      if (selectedUser) {
        await updateUser.mutateAsync({
          id: selectedUser.id,
          data: formData,
        });
      } else {
        await createUser.mutateAsync(formData);
      }
    } catch {
      // Error is handled in mutation's onError callback
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // A REAL role change deletes every per-user permission override in the same
    // transaction (users.service.ts:360-372) and there is no "keep current
    // permissions" option, so it is confirmed before it is sent (spec §1.1). The
    // comparison matches the backend's own (`data.role !== target.role`,
    // users.service.ts:337), so a name-only edit is never interrupted.
    if (selectedUser && formData.role !== selectedUser.role) {
      setPendingRoleChange(formData.role);
      return;
    }
    void submitForm();
  };

  const handleDelete = async (user: User) => {
    // Check if this is the only owner
    if (user.role === 'OWNER' && ownerCount === 1) {
      // Defence in depth: the row's Delete button is already `disabled` under
      // exactly this condition, so nothing in the table can reach this arm today.
      // It keeps the guard for a future caller and uses the same surface as the
      // other row refusals rather than a window.alert.
      enqueueSnackbar(
        'Cannot delete the last clinic owner. Please assign a new owner before deleting this user.',
        { variant: 'error' },
      );
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      try {
        await deleteUser.mutateAsync(user.id);
      } catch {
        // Surfaced by the mutation's onError (a snackbar) — same contract as the
        // form's submit. Without this the refusal is an unhandled rejection.
      }
    }
  };

  if (!hasPermission(TEAM_MEMBERS_PERMISSION)) {
    return (
      <Box p={3}>
        <Typography>You don't have permission to manage users.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">User Management</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenDialog()}
          disabled={isOffline}
        >
          Add New User
        </Button>
      </Box>

      {/* User ops touch Firebase/HubSpot/Postmark — online-only, never queued. */}
      <OfflineNotice>{TEAM_OFFLINE_NOTICE}</OfflineNotice>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Clinic</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    // Same map as the Select below, so the table and the dialog
                    // cannot name a role two different ways. `|| user.role` is not
                    // dead code: these rows can come from the offline replica
                    // (`db.users`), where `role` is whatever was replicated, so a
                    // value outside the union prints raw rather than `undefined`.
                    label={ROLE_LABELS[user.role] || user.role}
                    color={user.role === 'ADMIN' ? 'error' : 'primary'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{user.clinic.name}</TableCell>
                <TableCell>
                  {/* Replica rows lack the Firebase-enrichment fields
                      (hasCompletedSetup/invitationSentAt), so offline the
                      derived status would fabricate "Not Invited" for active
                      staff — show an honest unknown instead. */}
                  {isOffline ? (
                    <Chip label="Status unavailable offline" size="small" />
                  ) : (
                    <Chip
                      label={getUserStatus(user).label}
                      color={getUserStatus(user).color}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {!user.hasCompletedSetup && (
                      <Box display="flex" flexDirection="column" alignItems="flex-start">
                        <Tooltip title={getCooldownInfo(user)?.message || "Send Invitation Email"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => sendInvitation.mutate(user.id)}
                              color="info"
                              disabled={sendInvitation.isLoading || !canSendInvitation(user) || isOffline}
                            >
                              <SendIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {getCooldownInfo(user) && !getCooldownInfo(user)!.canSend && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontSize: '0.7rem' }}>
                            Wait {(() => {
                              const timeSinceLastSend = currentTime - user.lastInvitationSentAt!;
                              const twoMinutesInMs = 2 * 60 * 1000;
                              
                              // Handle clock skew: clamp to prevent negative values
                              if (timeSinceLastSend < 0) {
                                return '2m 0s';
                              }
                              
                              const secondsRemaining = Math.max(0, Math.ceil((twoMinutesInMs - timeSinceLastSend) / 1000));
                              const minutes = Math.floor(secondsRemaining / 60);
                              const seconds = secondsRemaining % 60;
                              return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
                            })()}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(user)}
                      color="primary"
                      disabled={isOffline}
                    >
                      <EditIcon />
                    </IconButton>
                    {/* P6: the editor's entry point. The Tooltip wraps a <span>
                        because a disabled control cannot take focus and MUI's
                        Tooltip would render nothing on it — the same shape the
                        invitation button above already uses. */}
                    {canManagePermissions && (
                      <Tooltip title="Permissions">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Permissions"
                            onClick={() => setPermissionsTarget(user)}
                            color="primary"
                            disabled={isOffline}
                          >
                            <PermissionsIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {can.clearPin && (
                      <Tooltip title="Clear PIN">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Clear PIN"
                            onClick={() => void handleClearPin(user)}
                            color="primary"
                            disabled={isOffline || clearPin.isLoading}
                          >
                            <ClearPinIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(user)}
                      color="error"
                      disabled={(user.role === 'OWNER' && ownerCount === 1) || isOffline}
                      title={user.role === 'OWNER' && ownerCount === 1 ? 'Cannot delete the last owner' : 'Delete user'}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={users.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedUser ? 'Edit User' : 'Add New User'}
          </DialogTitle>
          <DialogContent>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
                {errorMessage}
              </Alert>
            )}
            <OfflineNotice sx={{ mb: 0 }}>
              User accounts can't be created or changed offline.
            </OfflineNotice>
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <TextField
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!selectedUser}
                required
              />
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <FormControl required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                  label="Role"
                >
                  {/* `isRoleDisabled` is applied uniformly now. It is unchanged:
                      it encodes ONLY the last-owner rule, and it already answered
                      false for OWNER, which is why OWNER carried no `disabled`
                      prop before. */}
                  {roleOptions.map((role) => (
                    <MenuItem key={role} value={role} disabled={isRoleDisabled(role)}>
                      {ROLE_LABELS[role]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isOnlyOwner && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This user is the only clinic owner. Please assign another owner before changing this role.
                </Alert>
              )}
              {/* Clinic is auto-assigned; no selector */}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" disabled={isOffline}>
              {selectedUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Role-change confirm (spec §1.1) ───────────────────────────────── */}
      <Dialog open={!!roleChange} onClose={() => setPendingRoleChange(null)} maxWidth="sm" fullWidth>
        {roleChange && selectedUser && (
          <>
            <DialogTitle>
              {`Change ${selectedUser.name}'s role to ${
                ROLE_LABELS[roleChange.nextRole] || roleChange.nextRole
              }?`}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {`${selectedUser.name}'s permissions will be reset to the ${
                  ROLE_LABELS[roleChange.nextRole] || roleChange.nextRole
                } defaults.`}
                {roleChange.customized > 0
                  ? ` ${roleChange.customized} customized permission${
                      roleChange.customized === 1 ? '' : 's'
                    } will be removed.`
                  : ''}
              </DialogContentText>
              {/* ONE sentence, the editor's own — no lead, because the line is about
                  the person's app version rather than about what this change moves,
                  so it is true on both arms without a word of scaffolding. The
                  measured arm passes its list; the arm that can measure NOTHING opts
                  in with `alwaysShow`, which is why an empty list there cannot be
                  mistaken for "nothing narrows". */}
              {roleChange.showIosNote && (
                <IosVersionNote
                  firstName={firstNameOf(selectedUser.name)}
                  syncNarrowed={roleChange.syncNarrowed}
                  alwaysShow={!roleChange.precise}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingRoleChange(null)}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setPendingRoleChange(null);
                  void submitForm();
                }}
              >
                Change role
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* The roster and `/users/me` are passed DOWN rather than re-read: `users`
          is cached under the bare string key 'users' (the read above), and GET
          /users makes one Firebase lookup per clinic user, so a second spelling
          of that key would refetch the lot. Re-keying it is deliberately not in
          this PR (P6 design §A). */}
      {permissionsTarget && (
        <UserPermissionsDialog
          open
          onClose={() => setPermissionsTarget(null)}
          user={permissionsTarget}
          users={users}
          me={me as MeShape | undefined}
        />
      )}

    </Box>
  );
}; 